/**
 * Busca rapida da lista de conversas (estilo WhatsApp).
 *
 * Aceleres 2026-08-28: extraido de ChatList.vue. Guarda o estado da busca, o
 * debounce e a defesa contra resultado defasado; a NAVEGACAO fica no pai, que
 * e quem tem o router.
 *
 * `isPersonalBoxActive` chega como funcao de proposito, NAO e calculado aqui.
 * Ele decide se a consulta leva `aceleresBox: 'pessoais'` -- sem esse parametro
 * a busca deixa de restringir a caixa Pessoais e passa a devolver conversa
 * pessoal de outro vendedor. Quem sabe a caixa ativa e o pai (tem a rota);
 * duplicar essa leitura aqui seria criar uma segunda fonte de verdade para uma
 * regra de visibilidade.
 *
 * `onReset` existe para o pai limpar o fluxo de nova conversa
 * (startConversationFlowRef) sem que este composable precise conhece-lo.
 */
import { ref, computed } from 'vue';
import SearchAPI from 'dashboard/api/search';
import wootConstants from 'dashboard/constants/globals';

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

export function normalizeQuickSearchQuery(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim();
}

export function useAclQuickSearch({ isPersonalBoxActive, onReset } = {}) {
  const quickSearch = ref('');
  const quickSearchResults = ref({ contacts: [], conversations: [] });
  const quickSearchOpen = ref(false);
  const quickSearchLoading = ref(false);
  const quickSearchError = ref(false);
  let quickSearchTimer = null;
  let quickSearchSeq = 0;

  async function performQuickSearch(q) {
    quickSearchOpen.value = true;
    quickSearchLoading.value = true;
    quickSearchError.value = false;
    const seq = ++quickSearchSeq;
    const personalBoxActive = Boolean(isPersonalBoxActive?.());
    try {
      const r = await SearchAPI.conversations({
        q,
        status: wootConstants.STATUS_TYPE.ALL,
        excludePersonal: false,
        aceleresBox: personalBoxActive ? 'pessoais' : undefined,
      });
      if (seq !== quickSearchSeq) return; // resultado defasado, ignora
      const payload = r?.data?.payload || {};
      quickSearchResults.value = {
        contacts: payload.contacts || [],
        conversations: payload.conversations || [],
      };
    } catch (e) {
      if (seq === quickSearchSeq) {
        quickSearchResults.value = { contacts: [], conversations: [] };
        quickSearchError.value = true;
      }
      console.warn('quickSearch fail', e);
    } finally {
      if (seq === quickSearchSeq) quickSearchLoading.value = false;
    }
  }

  function onQuickSearchInput() {
    const q = normalizeQuickSearchQuery(quickSearch.value);
    if (quickSearchTimer) clearTimeout(quickSearchTimer);
    if (q.length < MIN_QUERY) {
      quickSearchSeq += 1;
      quickSearchLoading.value = false;
      quickSearchError.value = false;
      quickSearchResults.value = { contacts: [], conversations: [] };
      quickSearchOpen.value = q.length >= 1; // mostra CTA novo numero se for phone-like
      return;
    }
    quickSearchOpen.value = true;
    quickSearchLoading.value = true;
    quickSearchTimer = setTimeout(() => performQuickSearch(q), DEBOUNCE_MS);
  }

  function onQuickSearchEnter() {
    const q = normalizeQuickSearchQuery(quickSearch.value);
    if (q.length < MIN_QUERY) return;
    // Enter sempre confirma a pesquisa, inclusive para telefone. O botao
    // "Iniciar nova conversa" continua separado e so aparece sem resultados.
    if (quickSearchTimer) clearTimeout(quickSearchTimer);
    performQuickSearch(q);
  }

  function closeQuickSearch() {
    quickSearchOpen.value = false;
    quickSearch.value = '';
    quickSearchError.value = false;
    quickSearchResults.value = { contacts: [], conversations: [] };
    onReset?.();
  }

  // Sem consumidor hoje -- ja era morto no ChatList antes da extracao.
  // Mantido por simetria com closeQuickSearch; remover se seguir sem uso.
  function softCloseQuickSearch() {
    quickSearchOpen.value = false;
    onReset?.();
  }

  const quickSearchIsPhone = computed(() => {
    const digits = (quickSearch.value || '').replace(/\D/g, '');
    return digits.length >= 8;
  });

  const quickSearchNoResults = computed(
    () =>
      !quickSearchLoading.value &&
      !quickSearchError.value &&
      quickSearch.value.length >= MIN_QUERY &&
      quickSearchResults.value.contacts.length === 0 &&
      quickSearchResults.value.conversations.length === 0
  );

  const isSearchActive = computed(
    () => quickSearchOpen.value && quickSearch.value.length >= 1
  );

  // Aceleres: contatos do quick search ocultam os sem telefone (so email)
  const quickSearchContacts = computed(() =>
    (quickSearchResults.value.contacts || []).filter(c => c.phone_number)
  );

  // Um contato pode ter varios ATENDIMENTOS abertos ao mesmo tempo -- por
  // instancias diferentes, ou dois na mesma instancia. Ate 31/08/2026 esta
  // lista ficava com a primeira conversa de cada contato e descartava o resto,
  // para a mesma pessoa nao aparecer repetida; o efeito era o vendedor achar o
  // cliente e enxergar UM atendimento, sem indicio de que havia outros.
  //
  // Agora nao se esconde nada: cada conversa segue como uma linha propria e o
  // que a distingue (numero, instancia, situacao, vendedor) e desenhado no
  // card, em `quickSearchConversationMeta` no ChatList. Sem dedupe: quem limita
  // o volume e a pagina de 15 do backend, ordenada por atividade recente.
  const quickSearchConversations = computed(
    () => quickSearchResults.value.conversations || []
  );

  return {
    quickSearch,
    quickSearchResults,
    quickSearchOpen,
    quickSearchLoading,
    quickSearchError,
    quickSearchIsPhone,
    quickSearchNoResults,
    quickSearchContacts,
    quickSearchConversations,
    isSearchActive,
    onQuickSearchInput,
    onQuickSearchEnter,
    closeQuickSearch,
    softCloseQuickSearch,
  };
}
